/**
 * Terugkeren waar je was gebleven (Jordy 11-10-2026).
 *
 * Wie op Earnings een transactie opent (naar het gesprek) en daarna op
 * terug/vorige drukt, moet op precies dezelfde plek uitkomen: dezelfde
 * filters, dezelfde scrollpositie en de laatst geopende rij gemarkeerd.
 *
 * De filters staan in het adres (zie Earnings.tsx), dus die komen met de
 * browsergeschiedenis vanzelf terug. Hier alleen wat het adres niet draagt:
 * de scrollpositie en welke rij je opende. Per tabblad (sessionStorage), en
 * alleen geldig voor precies hetzelfde adres; een andere filter of een nieuwe
 * tab begint gewoon bovenaan.
 */
const SLEUTEL = 'earnings.terugkeer'
/** Na zo lang telt een bewaarde plek niet meer. */
const GELDIG_MS = 6 * 60 * 60 * 1000

export interface Plek {
  adres: string; y: number; rij: string; t: number
  /** `data-rij` van de aangeklikte tabelrij en waar die op het scherm stond. */
  anker?: string | null; ankerTop?: number | null
}

function adresNu(): string {
  return window.location.pathname + window.location.search
}

/** Vlak voor het openen van een transactie/gesprek aanroepen. `el` is het
 *  aangeklikte element; zijn tabelrij (`data-rij`) dient als anker. */
export function bewaarPlek(rij: string | number, el?: Element | null): void {
  try {
    const tr = el?.closest('[data-rij]') as HTMLElement | null
    const p: Plek = {
      adres: adresNu(), y: Math.round(window.scrollY), rij: String(rij), t: Date.now(),
      anker: tr?.dataset.rij ?? null,
      ankerTop: tr ? Math.round(tr.getBoundingClientRect().top) : null,
    }
    sessionStorage.setItem(SLEUTEL, JSON.stringify(p))
  } catch { /* geen opslag: dan gewoon bovenaan */ }
}

/** De bewaarde plek, als die bij dit adres hoort en niet te oud is. */
export function plekVoorHier(): Plek | null {
  try {
    const p = JSON.parse(sessionStorage.getItem(SLEUTEL) || 'null') as Plek | null
    if (!p || p.adres !== adresNu() || Date.now() - p.t > GELDIG_MS) return null
    return p
  } catch {
    return null
  }
}

/**
 * Terug naar de bewaarde plek. Met een anker: de aangeklikte rij weer op
 * dezelfde hoogte op het scherm. De panelen erboven laden op verschillende
 * momenten, dus er wordt bijgestuurd tot de pagina stil staat (of `maxMs`
 * voorbij is), en meteen gestopt zodra de gebruiker zelf scrolt.
 * Zonder anker: naar dezelfde scrollhoogte.
 */
export function herstelPlek(p: Plek, maxMs = 6000): () => void {
  const start = Date.now()
  let stop = false
  let stil = 0
  const zelf = () => { stop = true }
  const opties = { passive: true, once: true } as const
  window.addEventListener('wheel', zelf, opties)
  window.addEventListener('touchmove', zelf, opties)
  window.addEventListener('keydown', zelf, opties)
  const poging = () => {
    if (stop) return
    const el = p.anker
      ? (document.querySelector(`[data-rij="${CSS.escape(p.anker)}"]`) as HTMLElement | null)
      : null
    let verschil: number
    if (el && p.ankerTop != null) {
      verschil = el.getBoundingClientRect().top - p.ankerTop
    } else {
      const kan = document.documentElement.scrollHeight - window.innerHeight
      verschil = Math.min(p.y, Math.max(0, kan)) - window.scrollY
    }
    if (Math.abs(verschil) > 2) { window.scrollBy(0, verschil); stil = 0 } else { stil += 1 }
    if (stil >= 12 || Date.now() - start > maxMs) return opruimen()
    window.setTimeout(poging, 120)
  }
  const opruimen = () => {
    window.removeEventListener('wheel', zelf)
    window.removeEventListener('touchmove', zelf)
    window.removeEventListener('keydown', zelf)
  }
  poging()
  return () => { stop = true; opruimen() }
}
